import { useEffect, useState } from "react";
import {
   Table,
   Button,
   Modal,
   Form,
   Input,
   message,
   Popconfirm,
   Row,
   Col,
   Space,
   Tag,
   Tooltip
} from "antd";
import {
   EditOutlined,
   DeleteOutlined,
   PlusOutlined,
   ProjectOutlined,
   TeamOutlined,
   SearchOutlined
} from "@ant-design/icons";

// ─── API Imports ───
import {
   getProjects,
   createProject,
   updateProject,
   deleteProject
} from "../../services/projectsApi";

const Projects = () => {
   const [projects, setProjects] = useState([]);
   const [loading, setLoading] = useState(false);
   const [open, setOpen] = useState(false);
   const [editingProject, setEditingProject] = useState(null);
   const [searchText, setSearchText] = useState("");
   const [submitLoading, setSubmitLoading] = useState(false);
   const [form] = Form.useForm();

   const fetchProjects = async () => {
      setLoading(true);
      try {
         const res = await getProjects();
         setProjects(res.data);
      } catch (err) {
         message.error("Failed to fetch projects");
      } finally {
         setLoading(false);
      }
   };

   useEffect(() => {
      fetchProjects();
   }, []);

   const handleSubmit = async (values) => {
      setSubmitLoading(true);
      try {
         if (editingProject) {
            await updateProject(editingProject.id, values);
            message.success("Project updated successfully");
         } else {
            await createProject(values);
            message.success("Project created successfully");
         }
         handleCloseModal();
         fetchProjects();
      } catch (err) {
         message.error("Operation failed");
      } finally {
         setSubmitLoading(false);
      }
   };

   const handleDelete = async (id) => {
      try {
         await deleteProject(id);
         message.success("Project deleted");
         fetchProjects();
      } catch {
         message.error("Delete failed");
      }
   };

   const handleCloseModal = () => {
      setOpen(false);
      setEditingProject(null);
      form.resetFields();
   };

   const handleEdit = (record) => {
      setEditingProject(record);
      form.setFieldsValue(record);
      setOpen(true);
   };

   const filteredProjects = projects.filter((project) =>
      project.name?.toLowerCase().includes(searchText.toLowerCase())
   );

   const activeProjects = projects.filter((p) => p.lead_count > 0).length;

   const columns = [
      {
         title: "#",
         width: 50,
         align: "center",
         render: (_, __, index) => (
            <span className="text-xs text-gray-400">{index + 1}</span>
         )
      },
      {
         title: "Project Name",
         dataIndex: "name",
         render: (name) => (
            <Space>
               <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center">
                  <ProjectOutlined className="text-white text-sm" />
               </div>
               <span className="font-medium text-gray-800">{name}</span>
            </Space>
         )
      },
      {
         title: "Total Leads",
         dataIndex: "lead_count",
         render: (count) => (
            <Tag color={count > 0 ? "blue" : "default"}>
               {count || 0} leads
            </Tag>
         )
      },
      {
         title: "Status",
         render: (_, record) => (
            <Tag color={record.lead_count > 0 ? "green" : "default"}>
               {record.lead_count > 0 ? "Active" : "Inactive"}
            </Tag>
         )
      },
      {
         title: "Actions",
         width: 120,
         align: "center",
         render: (_, record) => (
            <Space size="small">
               <Tooltip title="Edit">
                  <Button
                     type="text"
                     size="small"
                     icon={<EditOutlined />}
                     onClick={() => handleEdit(record)}
                  />
               </Tooltip>

               <Popconfirm
                  title="Delete Project?"
                  description="Are you sure?"
                  onConfirm={() => handleDelete(record.id)}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{ danger: true }}
               >
                  <Tooltip title="Delete">
                     <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                     />
                  </Tooltip>
               </Popconfirm>
            </Space>
         )
      }
   ];

   return (
      <div className="bg-gray-50 min-h-screen">
         <style>{`
            .projects-card {
               background: #fff;
               border-radius: 10px;
               box-shadow: 0 1px 2px 0 rgba(0,0,0,0.03);
               border: 1px solid #f3f4f6;
            }
            .projects-table .ant-table-thead > tr > th {
               background: #f9fafb;
               font-size: 12px;
               padding: 10px 16px;
            }
            .projects-table .ant-table-tbody > tr > td {
               padding: 10px 16px;
               font-size: 13px;
            }
            .projects-table .ant-table-tbody > tr:hover > td {
               background: #f9fafb;
            }
         `}</style>

         {/* Header */}
         <div className="bg-white border-b px-6 py-4">
            <div className="flex items-center justify-between">
               <div>
                  <h1 className="text-xl font-semibold text-gray-800 m-0">Projects</h1>
                  <span className="text-gray-400 text-sm">{projects.length} total</span>
               </div>
               <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setOpen(true)}
               >
                  Add Project
               </Button>
            </div>
         </div>

         <div className="p-6">
            {/* Stats */}
            <Row gutter={[16, 16]} className="mb-4">
               <Col xs={24} sm={8}>
                  <div className="projects-card p-4">
                     <div className="flex items-center gap-3">
                        <ProjectOutlined className="text-indigo-500 text-xl" />
                        <div>
                           <div className="text-2xl font-bold text-gray-800">{projects.length}</div>
                           <div className="text-xs text-gray-500">Total Projects</div>
                        </div>
                     </div>
                  </div>
               </Col>
               <Col xs={24} sm={8}>
                  <div className="projects-card p-4">
                     <div className="flex items-center gap-3">
                        <TeamOutlined className="text-green-500 text-xl" />
                        <div>
                           <div className="text-2xl font-bold text-gray-800">{activeProjects}</div>
                           <div className="text-xs text-gray-500">Active Projects</div>
                        </div>
                     </div>
                  </div>
               </Col>
            </Row>

            {/* Table Card */}
            <div className="projects-card projects-table">
               <div className="p-4 border-b">
                  <div className="flex items-center justify-between">
                     <div className="flex items-center gap-2">
                        <ProjectOutlined className="text-gray-600" />
                        <span className="font-medium text-gray-700">All Projects</span>
                        <Tag>{filteredProjects.length}</Tag>
                     </div>
                     <Input
                        placeholder="Search..."
                        prefix={<SearchOutlined className="text-gray-400" />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                        style={{ width: 200 }}
                     />
                  </div>
               </div>

               <Table
                  size="small"
                  dataSource={filteredProjects}
                  columns={columns}
                  rowKey="id"
                  loading={loading}
                  pagination={{
                     pageSize: 10,
                     showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                     size: "small"
                  }}
                  locale={{
                     emptyText: (
                        <div className="py-8 text-center">
                           <ProjectOutlined className="text-4xl text-gray-300 mb-3" />
                           <p className="text-gray-500">No projects found</p>
                           <Button
                              type="primary"
                              size="small"
                              icon={<PlusOutlined />}
                              onClick={() => setOpen(true)}
                              className="mt-3"
                           >
                              Create First Project
                           </Button>
                        </div>
                     )
                  }}
               />
            </div>
         </div>

         {/* Add/Edit Modal */}
         <Modal
            title={editingProject ? "Edit Project" : "Add New Project"}
            open={open}
            onCancel={handleCloseModal}
            footer={null}
            width={400}
            destroyOnClose
         >
            <Form
               form={form}
               layout="vertical"
               onFinish={handleSubmit}
               className="mt-4"
            >
               <Form.Item
                  name="name"
                  label="Project Name"
                  rules={[
                     { required: true, message: "Please enter project name" },
                     { min: 3, message: "Minimum 3 characters" }
                  ]}
               >
                  <Input placeholder="Enter project name" />
               </Form.Item>

               <div className="flex justify-end gap-2">
                  <Button onClick={handleCloseModal}>Cancel</Button>
                  <Button type="primary" htmlType="submit" loading={submitLoading}>
                     {editingProject ? "Update" : "Create"}
                  </Button>
               </div>
            </Form>
         </Modal>
      </div>
   );
};

export default Projects;